/**
 * GameConnection — the client's single pipe to a game host. Two implementations:
 *  - WsConnection     (multiplayer: WebSocket to apps/server, msgpack frames)
 *  - LocalConnection  (single-player: GameSession inside a Web Worker, postMessage)
 * Both speak ClientMessage / ServerMessage from @friction/protocol.
 */
import type { ClientMessage, ServerMessage } from '@friction/protocol';

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface GameConnection {
  readonly kind: 'ws' | 'local';
  readonly status: ConnectionStatus;
  send(msg: ClientMessage): void;
  /** Subscribe to server messages; returns an unsubscribe function. */
  onMessage(fn: (msg: ServerMessage) => void): () => void;
  onStatus(fn: (status: ConnectionStatus) => void): () => void;
  close(): void;
}

let current: GameConnection | null = null;

/** The active connection (set by the lobby / single-player launcher). */
export function getConnection(): GameConnection | null {
  return current;
}

export function setConnection(conn: GameConnection | null): void {
  if (current && current !== conn) current.close();
  current = conn;
}

/** Send if connected; silently drops otherwise (UI should reflect status). */
export function send(msg: ClientMessage): void {
  current?.send(msg);
}
